import { AllocationRuleDto, CarrierVisitDirection, CarrierVisitResponse, JourneyResponse } from '@yard/app/api'
import { AllocationRuleStore } from '@yard/stores/allocation-rule.store'
import { CapacitySettingsStore } from '@yard/stores/CapacitySettingsStore'
import { CarrierVisitStore } from '@yard/stores/carrier-visit.store'
import { JourneyStore } from '@yard/stores/journey.store'
import { OccupancyStore } from '@yard/stores/OccupancyStore'
import { YardBlockStore } from '@yard/stores/YardBlockStore'
import { action, computed, makeObservable, observable, override } from 'mobx'
import { BaseAllocationRulesViewStore } from './base-allocation-rules.view-store'

export class CarrierVisitAllocationRulesViewStore extends BaseAllocationRulesViewStore {
  selectedCarrierVisitId?: number
  selectedCarrierVisitDirection?: CarrierVisitDirection

  constructor(
    allocationRuleStore: AllocationRuleStore,
    occupancyStore: OccupancyStore,
    journeyStore: JourneyStore,
    readonly carrierVisitStore: CarrierVisitStore,
    yardBlockStore: YardBlockStore,
    capacitySettingsStore: CapacitySettingsStore,
  ) {
    super(
      allocationRuleStore,
      occupancyStore,
      journeyStore,
      yardBlockStore,
      capacitySettingsStore,
    )
    makeObservable(this, {
      selectedCarrierVisitId: observable,
      selectedCarrierVisitDirection: observable,

      setSelectedCarrierVisitId: action,
      setSelectedCarrierVisitDirection: action,

      selectedCarrierVisitJourneys: computed,
      selectedCarrierVisitJourneyIds: computed,
      selectedCarrierVisitAllocationRules: computed,
      selectedCarrierVisit: computed,
      otherCarrierVisits: computed,
      copyableSourceCarrierVisits: computed,
      matchingCountByRule: override,
      unallocatedContainers: override,
    })
  }

  get selectedCarrierVisit() {
    return this.carrierVisitStore?.items.find(cv => cv.id === this.selectedCarrierVisitId)
  }

  get otherCarrierVisits(): CarrierVisitResponse[] {
    const primaryTimestamp = (cv: CarrierVisitResponse): number => {
      const iso = cv.eta || cv.ata || cv.etd || cv.atd
      return iso ? new Date(iso).getTime() : 0
    }
    return this.carrierVisitStore.items
      .filter(cv => cv.id !== this.selectedCarrierVisitId)
      .slice()
      .sort((a, b) => primaryTimestamp(b) - primaryTimestamp(a))
  }

  sourceVisitRules(sourceVisitId: number): AllocationRuleDto[] {
    return this.allocationRuleStore.carrierVisitRulesById[sourceVisitId] ?? []
  }

  copyableSourceVisitRules(sourceVisitId: number): AllocationRuleDto[] {
    const all = this.sourceVisitRules(sourceVisitId)
    if (!this.selectedCarrierVisitDirection) return all
    return all.filter(r => r.facets?.direction === this.selectedCarrierVisitDirection)
  }

  get copyableSourceCarrierVisits(): CarrierVisitResponse[] {
    return this.otherCarrierVisits.filter(cv => this.copyableSourceVisitRules(cv.id).length > 0)
  }

  get selectedCarrierVisitAllocationRules(): AllocationRuleDto[] {
    return this.orderedAllocationRules.filter(
      rule => rule.facets?.carrierVisitId === this.selectedCarrierVisitId,
    )
  }

  get selectedCarrierVisitJourneys(): JourneyResponse[] {
    return (
      this.journeyStore.items.filter(journey =>
        this.selectedCarrierVisitDirection === CarrierVisitDirection.Inbound
          ? journey.inboundCarrierVisitId === this.selectedCarrierVisitId
          : journey.outboundCarrierVisitId === this.selectedCarrierVisitId,
      ) || []
    )
  }

  get selectedCarrierVisitJourneyIds(): Set<number> {
    return new Set(this.selectedCarrierVisitJourneys.map(j => j.id))
  }

  /**
   * Override: the CV drawer badge shows "how many of THIS visit + direction's containers
   * are matched to this rule". Start from the yard-wide buckets (so priority is computed
   * consistently with the global view) and filter each bucket to this visit's journeys.
   */
  get matchingCountByRule(): Record<string, number> {
    const visitJourneyIds = this.selectedCarrierVisitJourneyIds
    const buckets = this.yardWideJourneysByRule
    const result: Record<string, number> = {}
    Object.keys(buckets).forEach(ruleId => {
      if (ruleId === 'unassigned') return
      result[ruleId] = buckets[ruleId].filter(j => visitJourneyIds.has(j.id)).length
    })
    return result
  }

  /**
   * Override: contextualize the unallocated list to this visit — yard-wide 'unassigned'
   * journeys whose inbound/outbound visit matches the selected one.
   */
  get unallocatedContainers(): JourneyResponse[] {
    const visitJourneyIds = this.selectedCarrierVisitJourneyIds
    return (this.yardWideJourneysByRule['unassigned'] ?? []).filter(j => visitJourneyIds.has(j.id))
  }

  resetChanges() {
    this.setAllocationRules(
      this.allocationRuleStore.carrierVisitRules.filter(
        rule =>
          rule.facets?.carrierVisitId === this.selectedCarrierVisitId &&
          rule.facets?.direction === this.selectedCarrierVisitDirection,
      ),
    )
    super.resetChanges()
  }

  setSelectedCarrierVisitId(carrierVisitId?: number) {
    this.selectedCarrierVisitId = carrierVisitId
  }

  setSelectedCarrierVisitDirection(direction?: CarrierVisitDirection) {
    this.selectedCarrierVisitDirection = direction
  }

  openCarrierVisitRules(carrierVisitId: number, direction: CarrierVisitDirection) {
    this.setSelectedCarrierVisitId(carrierVisitId)
    this.setSelectedCarrierVisitDirection(direction)

    this.setAllocationRules(
      this.allocationRuleStore.carrierVisitRules.filter(
        rule =>
          rule.facets?.carrierVisitId === carrierVisitId && rule.facets?.direction === direction,
      ),
    )

    this.setAllocationRulesDrawerOpen(true)
  }

  async submitRules() {
    await Promise.all(
      this.deletedRulesIds.map(id => this.allocationRuleStore.deleteCarrierVisitAllocationRule(id)),
    )

    const storeRulesById = this.allocationRuleStore.carrierVisitRules.reduce<
      Record<string, AllocationRuleDto>
    >((acc, rule) => {
      acc[rule.id] = rule
      return acc
    }, {})

    await Promise.all(
      this.allocationRules
        .filter(
          (rule, index) =>
            !rule.id ||
            this.editedRulesIds.includes(rule.id) ||
            storeRulesById[rule.id]?.priority !== index,
        )
        .map(rule => this.allocationRuleStore.upsertAllocationRuleByAllocationRuleDto(rule)),
    )

    this.resetChanges()
  }

  upsertRule(rule: AllocationRuleDto): void {
    rule.facets = {
      ...rule.facets,
      carrierVisitId: this.selectedCarrierVisitId,
      direction: this.selectedCarrierVisitDirection,
    }
    super.upsertRule(rule)
  }

  copyTemplateRule(templateRule: AllocationRuleDto) {
    const templateRuleForGlobalRule: AllocationRuleDto = {
      ...templateRule,
      facets: {
        ...templateRule.facets,
        direction: this.selectedCarrierVisitDirection,
        carrierType: this.selectedCarrierVisit?.carrierType,
        carrierVisitId: this.selectedCarrierVisitId,
      },
    }

    super.copyTemplateRule(templateRuleForGlobalRule)
  }
}
